/**
 * IndexedDB persistence layer for VAON project data.
 *
 * This module provides durable browser-side storage that survives page
 * refreshes, browser restarts, and network outages. It stores:
 *
 *   1. Scene state (components, selection) — the core project data
 *   2. Project metadata (name, id, mode, floor plan config)
 *   3. Floor plan PDF bytes — stored as a separate binary blob
 *   4. Component images — stored as individual binary blobs
 */

import type { Component, ConnectionRecord, PhotoPin } from "@/lib/model/types";
import type { ConstructionEvent } from "@/lib/store/slices/timeline";
import type { TwinRelationship } from "@/lib/revit-bridge/protocol";
import { componentForPersistence } from "@/lib/persistence/diagram-hidden";
import { verifyProjectLuminaireSchedule, type ProjectLuminaireSchedule } from "@/lib/luminaire-specifier/project-configurations";
import { deleteCachedMedia, readCachedMedia } from "@/lib/media/cache";

// ── Types ────────────────────────────────────────────────────────────────────

export interface SavedProject {
  projectId: string;
  projectName: string;
  /** Auth user ID of the project owner — used to scope projects per user in IndexedDB */
  ownerId?: string;
  components: Record<string, Component>;
  connections: Record<string, ConnectionRecord>;
  photoPins?: PhotoPin[];
  events: ConstructionEvent[];
  /** Present Day marker position in the event sequence (0 = before all events) */
  presentDayPosition?: number;
  /**
   * Revit bridge twin relationships — keyed by VAON component UUID.
   * Persisted so Revit re-sync can carry over VAON-only state (eventCreated,
   * VAON-only fields) by mapping regenerated UUIDs through stable Revit ElementIds.
   */
  twins?: Record<string, TwinRelationship>;
  meta: {
    /** Original archive metadata retained through local editing and reopen. */
    archiveMetadata?: { description: string; properties: Record<string, string> };
    luminaireSchedule?: ProjectLuminaireSchedule;
    mode: string;
    floorPlan: unknown;     // FloorPlan | null
    floorCount: number;
    autoInferEnabled: boolean;
    floorPositionsLocked?: boolean;
    floorPlanViewport?: unknown | null;    // FloorPlanViewport | null
    diagramTagSettings?: unknown;              // DiagramTagSettingsState
    shortCircuitAnalysisProfile?: import("@/lib/model/short-circuit-analysis").ShortCircuitAnalysisProfile;
    shortCircuitAnalysisEvidence?: import("@/lib/persistence/short-circuit-analysis").SavedShortCircuitEvidence;
    electricalSettings?: unknown;              // ElectricalSettingsState (sizing engine, N-M defaults, rules)
    diagramVisibilitySettings?: unknown | null; // DiagramVisibilitySettings
    /**
     * Which diagram mode (Single Line or Riser) to render. Renamed from
     * `sldViewMode` in Phase 3 of the SLD → Diagram terminology rename.
     * `applyDiagramFieldShim` translates the legacy key on read for older
     * cached records.
     */
    diagramViewMode?: "single-line" | "riser";
    riserRoomClusterEps?: number;
    riserArrangementMode?: "auto-arrange" | "hand-arrange";
    riserConductorSpecStyle?: "connection-lines" | "feeder-schedule";
    manualTileAssignments?: Record<string, { tx: number; ty: number; floorNumber: number | null }>;
    manualSegmentOffsets?: Record<string, { v: Record<number, number>; h: Record<number, number>; lastKnownSegmentCount?: number }>;
    manualLayoutSeed?: { autoPositionsByUuid: Record<string, { tx: number; ty: number }> } | null;
    deferredColumnDeletions?: number[];
    /** True when the cached snapshot is the full Revit model (tethered); false
     *  for the pruned cloud copy. Restored so load calcs apply the concealed-load
     *  rollup only when the model is pruned. */
    fullModelPresent?: boolean;
    /** Per-panel concealed-load rollup (cached so cloud-only load calcs survive
     *  a same-machine IndexedDB reload). */
    concealedLoadRollups?: import("@/lib/inference/concealed-load-rollup").ConcealedLoadRollups | null;
  };
  savedAt: string;
  /** Original creation date (from .vaon import or project creation) */
  dateCreated?: string;
  /** Last modification date */
  dateModified?: string;
  /** ISO timestamp when the project was archived (undefined = active) */
  archivedAt?: string;
}

export interface ProjectSummary {
  projectId: string;
  projectName: string;
  savedAt: string;
  componentCount: number;
  busCount: number;
  photoCount: number;
  dateCreated: string;
  dateModified: string;
  /** ISO timestamp when archived, or undefined if active */
  archivedAt?: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const DB_NAME = "vaon-projects";
const DB_VERSION = 5; // bumped for clean-exit-markers (private-mode sentinel fallback)
const STORE_PROJECTS = "projects";
const STORE_PDF = "pdf-bytes";
const STORE_IMAGES = "component-images";
const STORE_THUMBS = "component-image-thumbs";
const STORE_SEGMENTS = "session-segments";
const STORE_HEARTBEAT = "session-heartbeat";
const STORE_CLEAN_EXIT_MARKERS = "clean-exit-markers";

// ── Database connection ──────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    // If we can't open the DB, try deleting and recreating it
    let resolved = false;

    function tryOpen() {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
          db.createObjectStore(STORE_PROJECTS, { keyPath: "projectId" });
        }
        if (!db.objectStoreNames.contains(STORE_PDF)) {
          db.createObjectStore(STORE_PDF, { keyPath: "projectId" });
        }
        if (!db.objectStoreNames.contains(STORE_IMAGES)) {
          db.createObjectStore(STORE_IMAGES, { keyPath: "id" });
        }
        // v3: session recording stores
        if (!db.objectStoreNames.contains(STORE_SEGMENTS)) {
          const segStore = db.createObjectStore(STORE_SEGMENTS, { keyPath: "segmentId" });
          segStore.createIndex("projectId", "projectId", { unique: false });
        }
        if (!db.objectStoreNames.contains(STORE_HEARTBEAT)) {
          db.createObjectStore(STORE_HEARTBEAT, { keyPath: "sessionId" });
        }
        // v4: persisted JPEG thumbnails
        if (!db.objectStoreNames.contains(STORE_THUMBS)) {
          db.createObjectStore(STORE_THUMBS, { keyPath: "id" });
        }
        // v5: clean-exit markers (IDB fallback when localStorage sentinel is unavailable, e.g. private mode)
        if (!db.objectStoreNames.contains(STORE_CLEAN_EXIT_MARKERS)) {
          db.createObjectStore(STORE_CLEAN_EXIT_MARKERS, { keyPath: "sessionId" });
        }
      };

      request.onsuccess = () => {
        resolved = true;
        resolve(request.result);
      };
      request.onerror = () => {
        dbPromise = null;
        reject(request.error);
      };
      request.onblocked = () => {
        console.warn("[VAON] IndexedDB upgrade blocked — attempting to delete and recreate...");
        // Try deleting and reopening
        const delReq = indexedDB.deleteDatabase(DB_NAME);
        delReq.onsuccess = () => {
          console.log("[VAON] Old DB deleted, reopening...");
          tryOpen();
        };
        delReq.onerror = () => {
          dbPromise = null;
          reject(new Error("IndexedDB upgrade blocked and delete failed"));
        };
      };
    }

    tryOpen();

    // Timeout: if nothing happens in 8 seconds, delete and retry
    setTimeout(() => {
      if (!resolved) {
        console.warn("[VAON] IndexedDB open timed out, deleting and retrying...");
        const delReq = indexedDB.deleteDatabase(DB_NAME);
        delReq.onsuccess = () => {
          const retryReq = indexedDB.open(DB_NAME, DB_VERSION);
          retryReq.onupgradeneeded = () => {
            const db = retryReq.result;
            if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
              db.createObjectStore(STORE_PROJECTS, { keyPath: "projectId" });
            }
            if (!db.objectStoreNames.contains(STORE_PDF)) {
              db.createObjectStore(STORE_PDF, { keyPath: "projectId" });
            }
            if (!db.objectStoreNames.contains(STORE_IMAGES)) {
              db.createObjectStore(STORE_IMAGES, { keyPath: "id" });
            }
            // v3: session recording stores
            if (!db.objectStoreNames.contains(STORE_SEGMENTS)) {
              const segStore = db.createObjectStore(STORE_SEGMENTS, { keyPath: "segmentId" });
              segStore.createIndex("projectId", "projectId", { unique: false });
            }
            if (!db.objectStoreNames.contains(STORE_HEARTBEAT)) {
              db.createObjectStore(STORE_HEARTBEAT, { keyPath: "sessionId" });
            }
            // v4: persisted JPEG thumbnails
            if (!db.objectStoreNames.contains(STORE_THUMBS)) {
              db.createObjectStore(STORE_THUMBS, { keyPath: "id" });
            }
            // v5: clean-exit markers (private-mode sentinel fallback)
            if (!db.objectStoreNames.contains(STORE_CLEAN_EXIT_MARKERS)) {
              db.createObjectStore(STORE_CLEAN_EXIT_MARKERS, { keyPath: "sessionId" });
            }
          };
          retryReq.onsuccess = () => {
            resolved = true;
            resolve(retryReq.result);
          };
          retryReq.onerror = () => {
            dbPromise = null;
            reject(new Error("IndexedDB retry failed"));
          };
        };
        delReq.onerror = () => {
          dbPromise = null;
          reject(new Error("IndexedDB timeout and delete failed"));
        };
      }
    }, 8000);
  });

  return dbPromise;
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function saveProjectLocal(
  projectId: string,
  projectName: string,
  components: Record<string, Component>,
  connections: Record<string, ConnectionRecord>,
  events: ConstructionEvent[],
  meta: SavedProject["meta"],
  photoPins?: PhotoPin[],
  dates?: { dateCreated?: string; dateModified?: string },
  presentDayPosition?: number,
  ownerId?: string,
  twins?: Record<string, TwinRelationship>
): Promise<void> {
  // Validate before opening a write transaction; invalid present data must never
  // replace the last recoverable project with a silently emptied schedule.
  const metaSnapshot = { ...meta };
  let luminaireSchedule = metaSnapshot.luminaireSchedule === undefined
    ? undefined : await verifyProjectLuminaireSchedule(metaSnapshot.luminaireSchedule);
  const db = await getDB();
  const now = new Date().toISOString();

  // Preserve existing dateCreated and ownerId if this is an update (not a new save)
  let dateCreated = dates?.dateCreated ?? now;
  let effectiveOwnerId = ownerId;
  const tx0 = db.transaction(STORE_PROJECTS, "readonly");
  const existing = await new Promise<SavedProject | null>((resolve, reject) => {
    const req = tx0.objectStore(STORE_PROJECTS).get(projectId);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error ?? new Error("Could not read the existing project before saving."));
  });
  if (existing?.dateCreated) dateCreated = existing.dateCreated;
  if (metaSnapshot.archiveMetadata === undefined && existing?.meta.archiveMetadata !== undefined) {
    metaSnapshot.archiveMetadata = existing.meta.archiveMetadata;
  }
  // Preserve existing ownerId if not provided (e.g. from auto-save).
  if (!effectiveOwnerId && existing?.ownerId) effectiveOwnerId = existing.ownerId;
  // Omission is a legacy caller that does not own lighting state. Only an
  // explicitly supplied verified empty schedule clears the stored copy.
  if (luminaireSchedule === undefined && existing?.meta?.luminaireSchedule !== undefined) {
    luminaireSchedule = await verifyProjectLuminaireSchedule(existing.meta.luminaireSchedule);
  }

  const persistedComponents = Object.fromEntries(
    Object.entries(components).map(([uuid, component]) => [uuid, componentForPersistence(component)]),
  );
  const record: SavedProject = {
    projectId,
    projectName,
    ownerId: effectiveOwnerId,
    components: persistedComponents,
    connections,
    photoPins: photoPins ?? [],
    events,
    presentDayPosition: presentDayPosition ?? 0,
    twins,
    meta: { ...metaSnapshot, luminaireSchedule },
    savedAt: now,
    dateCreated,
    dateModified: dates?.dateModified ?? now,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readwrite");
    tx.objectStore(STORE_PROJECTS).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadProjectLocal(
  projectId: string
): Promise<SavedProject | null> {
  const db = await getDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readonly");
    const request = tx.objectStore(STORE_PROJECTS).get(projectId);
    request.onsuccess = async () => {
      const record = request.result as SavedProject | undefined;
      try {
        if (record) {
          applyDiagramFieldShim(record);
          if (record.meta?.luminaireSchedule !== undefined) {
            record.meta.luminaireSchedule = await verifyProjectLuminaireSchedule(record.meta.luminaireSchedule);
          }
        }
        resolve(record ?? null);
      } catch (error) { reject(error); }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Backward-compat shim for the SLD → Diagram rename rollout.
 *
 * Records persisted by older web builds use the legacy `sld*` field names
 * (`sldLocation`, `sldHidden`, `sldViewMode`). The post-rename in-memory
 * shape uses `diagram*` names. This shim translates legacy keys to new
 * keys on read so older saved data hydrates correctly.
 *
 * Newer key wins on hybrid records (where both names happen to be present).
 *
 * Exported only for unit-test access.
 */
export function applyDiagramFieldShim(record: SavedProject): void {
  const components = (record.components ?? {}) as unknown as Record<string, Record<string, unknown>>;
  for (const c of Object.values(components)) {
    if (c.diagramLocation === undefined && c.sldLocation !== undefined) {
      c.diagramLocation = c.sldLocation;
    }
    if (c.diagramHidden === undefined && c.sldHidden !== undefined) {
      c.diagramHidden = c.sldHidden;
    }
  }
  const meta = record.meta as Record<string, unknown> | undefined;
  if (meta && meta.diagramViewMode === undefined && meta.sldViewMode !== undefined) {
    meta.diagramViewMode = meta.sldViewMode;
  }
  if (meta && meta.diagramTagSettings === undefined && meta.sldTagSettings !== undefined) {
    meta.diagramTagSettings = meta.sldTagSettings;
  }
  if (meta && meta.diagramVisibilitySettings === undefined && meta.sldVisibilitySettings !== undefined) {
    meta.diagramVisibilitySettings = meta.sldVisibilitySettings;
  }
}

export async function savePdfBytes(
  projectId: string,
  bytes: Uint8Array,
  shouldWrite?: () => boolean,
  assetVersion?: string,
): Promise<void> {
  const db = await getDB();
  // A PDF download may become stale while the database is opening. Check
  // immediately before its transaction so it cannot overwrite a newer import.
  if (shouldWrite && !shouldWrite()) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PDF, "readwrite");
    tx.objectStore(STORE_PDF).put({ projectId, bytes, ...(assetVersion ? { assetVersion } : {}) });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadPdfBytes(
  projectId: string,
  assetVersion?: string,
): Promise<Uint8Array | null> {
  const db = await getDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PDF, "readonly");
    const request = tx.objectStore(STORE_PDF).get(projectId);
    request.onsuccess = () => {
      const record = request.result;
      resolve(assetVersion && record?.assetVersion !== assetVersion ? null : record?.bytes ?? null);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * List local projects from IndexedDB, optionally filtered by owner.
 * When ownerId is provided, only projects whose ownerId matches exactly are
 * returned. Projects without a stamped ownerId (orphans, e.g. from before
 * user-scoping or from another account's unstamped records) are excluded
 * from a scoped query — they remain visible only when the caller passes no
 * ownerId (e.g. the dev-batch-import tooling).
 */
export async function listLocalProjects(ownerId?: string): Promise<ProjectSummary[]> {
  const db = await getDB();

  const summaries = await new Promise<ProjectSummary[]>((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readonly");
    const request = tx.objectStore(STORE_PROJECTS).getAll();
    request.onsuccess = () => {
      const all = request.result as SavedProject[];

      const filtered = ownerId
        ? all.filter((p) => p.ownerId === ownerId)
        : all;

      const projects: ProjectSummary[] = filtered.map((p) => {
        const comps = Object.values(p.components);
        let photoCount = 0;
        let busCount = 0;
        for (const comp of comps) {
          photoCount += (comp.imageFilenames?.length ?? 0);
          if (comp.identifier === "bus") busCount++;
        }
        // Also count photo pin images
        for (const pin of (p.photoPins ?? [])) {
          photoCount += (pin.imageFilenames?.length ?? 0);
        }
        return {
          projectId: p.projectId,
          projectName: p.projectName,
          savedAt: p.savedAt,
          componentCount: comps.length,
          busCount,
          photoCount,
          dateCreated: p.dateCreated ?? p.savedAt,
          dateModified: p.dateModified ?? p.savedAt,
          archivedAt: p.archivedAt,
        };
      });
      projects.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
      resolve(projects);
    };
    request.onerror = () => reject(request.error);
  });
  await Promise.all(summaries.map(async summary => {
    const captures = await readCachedMedia(summary.projectId).catch(() => []);
    if (captures.length) summary.photoCount = captures.length;
  }));
  return summaries;
}

export async function deleteProjectLocal(projectId: string): Promise<void> {
  const db = await getDB();

  await new Promise<void>((resolve, reject) => {
    const stores = [STORE_PROJECTS, STORE_PDF];
    if (db.objectStoreNames.contains(STORE_IMAGES)) {
      stores.push(STORE_IMAGES);
    }
    if (db.objectStoreNames.contains(STORE_THUMBS)) {
      stores.push(STORE_THUMBS);
    }
    const tx = db.transaction(stores, "readwrite");
    tx.objectStore(STORE_PROJECTS).delete(projectId);
    tx.objectStore(STORE_PDF).delete(projectId);

    // Delete all images for this project
    if (db.objectStoreNames.contains(STORE_IMAGES)) {
      const imgStore = tx.objectStore(STORE_IMAGES);
      const imgRequest = imgStore.openCursor();
      imgRequest.onsuccess = () => {
        const cursor = imgRequest.result;
        if (cursor) {
          const record = cursor.value;
          if (record.projectId === projectId) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    }

    // Delete all thumbnails for this project
    if (db.objectStoreNames.contains(STORE_THUMBS)) {
      const thumbStore = tx.objectStore(STORE_THUMBS);
      const thumbRequest = thumbStore.openCursor();
      thumbRequest.onsuccess = () => {
        const cursor = thumbRequest.result;
        if (cursor) {
          const record = cursor.value;
          if (record.projectId === projectId) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  await deleteCachedMedia(projectId);
}

// ── Archive / restore ───────────────────────────────────────────────────────

export async function archiveProjectLocal(projectId: string): Promise<void> {
  const db = await getDB();
  const saved = await loadProjectLocal(projectId);
  if (!saved) return;
  saved.archivedAt = new Date().toISOString();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readwrite");
    tx.objectStore(STORE_PROJECTS).put(saved);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function renameProjectLocal(
  projectId: string,
  newName: string
): Promise<void> {
  const db = await getDB();
  const saved = await loadProjectLocal(projectId);
  if (!saved) return;
  saved.projectName = newName;
  saved.savedAt = new Date().toISOString();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readwrite");
    tx.objectStore(STORE_PROJECTS).put(saved);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function restoreProjectLocal(projectId: string): Promise<void> {
  const db = await getDB();
  const saved = await loadProjectLocal(projectId);
  if (!saved) return;
  delete saved.archivedAt;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readwrite");
    tx.objectStore(STORE_PROJECTS).put(saved);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Permanently delete projects archived more than `days` ago. */
export async function purgeArchivedProjects(days: number): Promise<number> {
  const db = await getDB();
  const cutoff = Date.now() - days * 86_400_000;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PROJECTS, "readonly");
    const request = tx.objectStore(STORE_PROJECTS).getAll();
    request.onsuccess = async () => {
      const toPurge = (request.result as SavedProject[]).filter(
        (p) => p.archivedAt && new Date(p.archivedAt).getTime() < cutoff
      );
      let purged = 0;
      for (const p of toPurge) {
        try {
          await deleteProjectLocal(p.projectId);
          purged++;
        } catch { /* skip failures */ }
      }
      resolve(purged);
    };
    request.onerror = () => reject(request.error);
  });
}

// ── Component image storage ─────────────────────────────────────────────────

export async function saveComponentImages(
  projectId: string,
  componentUuid: string,
  images: Map<string, Uint8Array>
): Promise<void> {
  const db = await getDB();
  if (!db.objectStoreNames.contains(STORE_IMAGES)) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_IMAGES, "readwrite");
    const store = tx.objectStore(STORE_IMAGES);

    for (const [filename, bytes] of images) {
      store.put({
        id: `${projectId}/${componentUuid}/${filename}`,
        projectId,
        componentUuid,
        filename,
        bytes,
      });
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Count every image record cached locally for a project (components + pins).
 * Used to compare against the cloud-backed count so the UI can surface how
 * many photos from an import have actually synced to Supabase.
 */
export async function countLocalProjectImages(projectId: string): Promise<number> {
  const db = await getDB();
  if (!db.objectStoreNames.contains(STORE_IMAGES)) return 0;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_IMAGES, "readonly");
    const store = tx.objectStore(STORE_IMAGES);
    const prefix = `${projectId}/`;
    const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
    const request = store.count(range);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function loadComponentImages(
  projectId: string,
  componentUuid: string
): Promise<Map<string, Uint8Array>> {
  const db = await getDB();
  const result = new Map<string, Uint8Array>();
  if (!db.objectStoreNames.contains(STORE_IMAGES)) return result;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_IMAGES, "readonly");
    const store = tx.objectStore(STORE_IMAGES);

    // Use a key range on the id field (format: projectId/componentUuid/filename)
    // to efficiently scan only records for this specific project + component.
    const prefix = `${projectId}/${componentUuid}/`;
    const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
    const request = store.openCursor(range);

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const record = cursor.value;
        result.set(record.filename, record.bytes);
        cursor.continue();
      } else {
        resolve(result);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

// ── Component thumbnail storage ─────────────────────────────────────────────
// JPEG thumbs generated client-side, keyed the same as component-images so IDB
// prefix scans work identically. Each record carries a `version` field; loads
// drop records below the current THUMB_VERSION so resolution/quality bumps in
// lib/media/thumbnails.ts force a regeneration without a DB upgrade.

import { THUMB_VERSION } from "@/lib/media/thumbnails";

export async function saveComponentThumbs(
  projectId: string,
  componentUuid: string,
  thumbs: Map<string, Uint8Array>
): Promise<void> {
  const db = await getDB();
  if (!db.objectStoreNames.contains(STORE_THUMBS)) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_THUMBS, "readwrite");
    const store = tx.objectStore(STORE_THUMBS);

    for (const [filename, bytes] of thumbs) {
      store.put({
        id: `${projectId}/${componentUuid}/${filename}`,
        projectId,
        componentUuid,
        filename,
        bytes,
        version: THUMB_VERSION,
      });
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadComponentThumbs(
  projectId: string,
  componentUuid: string
): Promise<Map<string, Uint8Array>> {
  const db = await getDB();
  const result = new Map<string, Uint8Array>();
  if (!db.objectStoreNames.contains(STORE_THUMBS)) return result;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_THUMBS, "readwrite");
    const store = tx.objectStore(STORE_THUMBS);

    const prefix = `${projectId}/${componentUuid}/`;
    const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
    const request = store.openCursor(range);

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const record = cursor.value;
        const recVersion = typeof record.version === "number" ? record.version : 0;
        if (recVersion < THUMB_VERSION) {
          cursor.delete();
        } else {
          result.set(record.filename, record.bytes);
        }
        cursor.continue();
      } else {
        resolve(result);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

export async function deleteComponentThumbs(
  projectId: string,
  componentUuid: string,
  filenames?: string[]
): Promise<void> {
  const db = await getDB();
  if (!db.objectStoreNames.contains(STORE_THUMBS)) return;

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_THUMBS, "readwrite");
    const store = tx.objectStore(STORE_THUMBS);

    if (filenames && filenames.length > 0) {
      for (const filename of filenames) {
        store.delete(`${projectId}/${componentUuid}/${filename}`);
      }
    } else {
      const prefix = `${projectId}/${componentUuid}/`;
      const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
      const request = store.openCursor(range);
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) { cursor.delete(); cursor.continue(); }
      };
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
